import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import {
  Button,
  Card,
  Group,
  H2,
  Modal,
  PlaceIcon,
  Stack,
  Text,
  TimeFiveIcon,
} from "@/components/ui/elements";
import { IconLabel } from "@/components/ui/parts";

import { Review } from "../review";
import classes from "./_css/WorkCompleteCard.module.css";
import { WorkTags } from "./WorkTags";

type Props = {
  id: string;
  title: string;
  tags: string[] | null;
  officeName: string;
  jobCategoryName: string;
  workPlaceAddress?: string | null;
  onReviewComplete: (average: number) => void;
  workCondition: {
    formatWorkDate: string;
  };
};

export type WorkCompleteCardRef = {
  openReview: () => void;
};

export const WorkCompleteCard = forwardRef<WorkCompleteCardRef, Props>(
  function WorkCompleteCard(
    {
      id,
      title,
      tags,
      officeName,
      jobCategoryName,
      workPlaceAddress,
      onReviewComplete,
      workCondition,
    },
    ref,
  ) {
    const [opened, { open, close }] = useDisclosure(false);
    // 地図・場所の報告モーダルを重ねている間は、Esc でレビュー入力ごと閉じて入力が消えないようにする
    const [mapLocationReportOpened, setMapLocationReportOpened] =
      useState(false);
    const isMobile = useMediaQuery("(max-width: 768px)");

    useImperativeHandle(ref, () => ({
      openReview: open,
    }));

    const handleReviewComplete = (avg: number) => {
      close();
      onReviewComplete(avg);
    };

    return (
      <>
        <div className={classes.container}>
          <Card
            withBorder
            shadow="lg"
            radius="md"
            p="md"
            bg="gray.0"
            className={classes.card}
          >
            <Card.Section className={classes.section} mt="sm">
              <Stack gap="sm">
                <Group justify="space-between">
                  <H2>{title}</H2>
                </Group>
                <Group gap="xs" pt={5}>
                  {tags && <WorkTags tags={tags} />}
                </Group>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Group>
                    <IconLabel
                      icon={<TimeFiveIcon color="teal" />}
                      label={workCondition.formatWorkDate}
                      labelProps={{ fz: "sm", fw: "bold" }}
                    />
                  </Group>
                </Group>
                <Group py={5} grow>
                  <IconLabel
                    icon={<PlaceIcon color="teal" />}
                    label={workPlaceAddress || ""}
                    labelProps={{ fz: "sm" }}
                  />
                </Group>
                <Group justify="space-between">
                  <Text fz="md" fw={800} c="teal.8">
                    {officeName}
                  </Text>
                </Group>
              </Stack>
            </Card.Section>

            <Group justify="flex-end" mt="sm" wrap="nowrap" gap="xs">
              <Button color="teal" onClick={open} size="lg">
                レビューする
              </Button>
            </Group>
          </Card>
        </div>
        <Modal
          opened={opened}
          close={close}
          size={isMobile ? "md" : "xl"}
          title="施設様の評価を入力をお願いします。"
          fullScreen={isMobile}
          closeOnEscape={!mapLocationReportOpened}
        >
          <Review
            close={close}
            temporaryJobOfferId={id}
            onReviewComplete={handleReviewComplete}
            formatWorkDate={workCondition.formatWorkDate}
            officeName={officeName}
            onMapLocationReportOpenedChange={setMapLocationReportOpened}
          />
        </Modal>
      </>
    );
  },
);
